/**
 * Plan upsell — the one place the dashboard sells.
 *
 * Same card language as the marketing site's feature grid: an engraved mark,
 * a monospace tier chip, a line of type, what you'd get, one action. No
 * gradients, no lock badges, no "PRO" glow.
 *
 * Two shapes:
 *   block — sits inside a page whose other content still works (Bookings).
 *   page  — stands in for a whole route the plan doesn't include (SEO).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { MONTHLY, PLAN_NAMES, type PlanId } from '@foundlio/pricing';
import { Eyebrow, H2, H3, Mark } from './kit';
import { cn } from '@/lib/utils';

interface PlanUpsellProps {
  /** Line-art mark from `lib/marks` — draw the feature, not a padlock. */
  mark: string;
  /** Cheapest plan that includes the feature. Drives the chip and the price. */
  requiredPlan: PlanId;
  title: string;
  description: string;
  /** What the upgrade actually buys, in the customer's words. */
  includes?: string[];
  variant?: 'block' | 'page';
  className?: string;
}

function TierChip({ plan }: { plan: PlanId }) {
  return (
    <span className="shrink-0 whitespace-nowrap rounded-full bg-zinc-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
      {PLAN_NAMES[plan]}
      {plan !== 'growth' && '+'}
    </span>
  );
}

function UpgradeButton({ plan }: { plan: PlanId }) {
  return (
    <Link
      to="/dashboard/account/billing"
      className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-[0.99]"
    >
      Upgrade to {PLAN_NAMES[plan]}
      <span aria-hidden className="font-mono text-xs opacity-70">
        ${MONTHLY[plan]}/mo
      </span>
    </Link>
  );
}

function Includes({ items, className }: { items: string[]; className?: string }) {
  return (
    <ul className={cn('space-y-1.5', className)}>
      {items.map((item) => (
        <li key={item} className="flex gap-2.5 text-[14px] leading-relaxed text-muted">
          <span aria-hidden className="mt-[9px] h-px w-3 shrink-0 bg-zinc-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

const PlanUpsell: React.FC<PlanUpsellProps> = ({
  mark,
  requiredPlan,
  title,
  description,
  includes,
  variant = 'block',
  className,
}) => {
  if (variant === 'page') {
    return (
      <div
        className={cn(
          'mx-auto max-w-xl rounded-xl border border-zinc-200 bg-white px-6 py-14 text-center sm:px-10',
          className
        )}
      >
        <Mark src={mark} size="xl" className="mx-auto" />
        <div className="mx-auto mb-5 mt-6 h-px w-10 bg-zinc-300" />
        <Eyebrow className="mb-3">Included from {PLAN_NAMES[requiredPlan]}</Eyebrow>
        <H2>{title}</H2>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-muted">{description}</p>
        {includes && includes.length > 0 && (
          <Includes items={includes} className="mx-auto mt-7 max-w-xs text-left" />
        )}
        <div className="mt-8">
          <UpgradeButton plan={requiredPlan} />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border border-zinc-200 bg-white p-6 sm:p-7', className)}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-7">
        <Mark src={mark} size="md" className="-ml-2 -mt-2 sm:-mt-1" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <H3>{title}</H3>
            <TierChip plan={requiredPlan} />
          </div>
          <p className="mt-2 text-[14px] leading-relaxed text-muted">{description}</p>
          {includes && includes.length > 0 && <Includes items={includes} className="mt-4" />}
          <div className="mt-5">
            <UpgradeButton plan={requiredPlan} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanUpsell;
